import { ExtendedShareholderDTO } from "../../../types/Shareholder";
import { makeAutoObservable, toJS } from "mobx";
import { newUuid } from "../../../utils/uuid";
import { IssueNewSharesDTO, IssueShareTypeDTO, Transaction } from "../../../types/Transactions";
import { compact, groupBy, identity, sortBy, sum } from "lodash";
import Big from "big.js";
import { IssuedBy } from "../../../types/const/Transactions";
import {
  ShareTypeBonusIssueModel,
  SubscriberForBonusIssueModel,
} from "../../../dbot/capitalIncreaseThroughBonusIssue/capitalIncreaseThroughBonusIssueDomain";
import { combineDateAndTime } from "../../../utils/Dates";
import { TMessage } from "../../../i18n/tt";
import {
  BooleanFieldModel,
  LocalDateFieldModel,
  NumberFieldModel,
  RadioFieldModel,
} from "../../../common/commonForms";
import { getShareSumByType } from "../../shareholderRegisterDomain";
import { ShareholderIdShares } from "../../../types/excelImportDomain";
import { ShareType, ShareTypeCode, shareTypeLabel } from "../../../common/share";
import { logDebug } from "../../../utils/log";
import { toShareholderReference } from "../../../utils/UrnReference";
import { TransactionDateCardModel } from "../commonTransactionsDomain";
import { TransactionType } from "../../../types/enums/TransactionType";

export class IssueOfNewSharesTransactionModel {
  readonly type = "issueOfNewShares";
  oldNominalValue;
  isRegistered: BooleanFieldModel;
  registrationDate: TransactionDateCardModel;
  shareholderRightsDate: TransactionDateCardModel;
  issuedBy: IssuedByModel;
  isBonusIssue: BooleanFieldModel;
  increaseType: RadioFieldModel<IncreaseType>;
  shareIssue: ShareIssueModel;
  isDirty = false;
  shareNominalValue: NumberFieldModel;
  hasNoExistingShares: boolean;

  constructor(
    readonly shareholderData: {
      activeShareTypes: ShareType[];
      shareholders: ExtendedShareholderDTO[];
      shareSumBySharetypeCode: { [shareTypeCode in ShareTypeCode]?: number };
      transactionLog: Transaction[];
      loadShareholdersForDate: (orgNumber: string, date: string) => void;
    },
    readonly companyData: {
      orgNumber: string;
      shareNominalValue: number;
      currency: string;
    },
    shareholderIdShares?: ShareholderIdShares[],
  ) {
    makeAutoObservable(this, {}, { autoBind: true });
    this.isRegistered = new BooleanFieldModel({ value: true });
    this.registrationDate = new TransactionDateCardModel({
      data: shareholderData,
      onChange: (date, time) => {
        this.setDirty();
        this.updateShareholderData(date, time);
      },
    });
    this.shareholderRightsDate = new TransactionDateCardModel({
      data: shareholderData,
      onChange: (date, time) => {
        this.setDirty();
        this.updateShareholderData(date, time);
      },
    });
    this.issuedBy = new IssuedByModel(this.setDirty);
    this.shareIssue = new ShareIssueModel(
      shareholderData,
      companyData,
      this.setDirty,
      shareholderIdShares,
    );
    this.isBonusIssue = new BooleanFieldModel({
      onChange: () => {
        this.setDirty();
        this.issuedBy.update();
      },
    });
    this.increaseType = createIncreaseTypeModel(IncreaseType.newIssue, this.setDirty);
    this.shareNominalValue = new NumberFieldModel({
      onChange: this.setDirty,
      value: companyData.shareNominalValue,
      isPositive: true,
    });
    this.oldNominalValue = companyData.shareNominalValue;

    const shareSums = getShareSumByType(shareholderData.shareholders);
    this.hasNoExistingShares = Object.values(shareSums).every((sum) => sum === 0);
  }

  setDirty() {
    this.isDirty = true;
  }

  clearDirty() {
    this.isDirty = false;
  }

  get error(): TMessage | undefined {
    return this.shareIssue.error;
  }

  updateShareholderData(date?: string, time?: string) {
    if (date && time) {
      const isoDate = combineDateAndTime(date, time);
      this.shareholderData.loadShareholdersForDate(this.companyData.orgNumber, isoDate);
    }
  }

  validate(): boolean {
    this.registrationDate.validate();
    this.shareholderRightsDate.validate();
    this.issuedBy.validate();
    this.shareIssue.validate();

    return (
      (this.isRegistered.value
        ? this.registrationDate.isValid
        : this.shareholderRightsDate.isValid) &&
      this.issuedBy.isValid &&
      this.shareIssue.isValid
    );
  }

  prepareData(): IssueNewSharesDTO["data"] {
    return {
      organizationnumber: this.companyData.orgNumber,
      transactionType: TransactionType.ISSUE,
      registrationDate: this.isRegistered.value
        ? this.registrationDate.combinedDateAndTime
        : undefined,
      shareholderRightsDate: this.isRegistered.value
        ? undefined
        : this.shareholderRightsDate.combinedDateAndTime,
      shareAllocation: this.shareIssue.prepareData(),
      isBonusIssue: this.isBonusIssue.value,
      increaseType: this.increaseType.value,
      registeredInNorwegian: this.isRegistered.value ?? false,
      shareNominalValue: this.hasNoExistingShares ? this.shareNominalValue.parsedValue : undefined,
      ...this.issuedBy.prepareData(),
    };
  }
}

export function createCurrencyModel(
  defaultCurrency: string,
  setDirty: () => void,
): RadioFieldModel<string> {
  return new RadioFieldModel<string>({
    value: defaultCurrency,
    options: [defaultCurrency],
    idFn: identity,
    labelFn: (c) => ({ translated: c }),
    onChange: setDirty,
  });
}

export enum IncreaseType {
  debtConversion = "debtConversion",
  newIssue = "newIssue",
}

export function createIncreaseTypeModel(
  increaseType: IncreaseType,
  setDirty: () => void,
): RadioFieldModel<IncreaseType> {
  return new RadioFieldModel<IncreaseType>({
    value: increaseType,
    options: Object.values(IncreaseType),
    idFn: identity,
    labelFn: (t) => ({ key: `transactions.issueTransaction.increaseType.${t}` }),
    onChange: setDirty,
  });
}

export class ShareTypeIssueModel {
  readonly shareType: ShareType;
  shareNominalValue: number;
  numberOfShares: NumberFieldModel;
  price: NumberFieldModel;
  currency: string;

  constructor(
    shareType: ShareType,
    currentCurrency: string,
    setDirty: () => void,
    shareNominalValue: number,
    numberOfShares?: number,
  ) {
    makeAutoObservable(this, {}, { autoBind: true });
    this.shareType = shareType;
    this.shareNominalValue = shareNominalValue;
    this.numberOfShares = new NumberFieldModel({
      onChange: setDirty,
      isInteger: true,
      isPositiveOrZero: true,
      value: numberOfShares,
    });
    this.price = new NumberFieldModel({
      onChange: setDirty,
      validator: (value) => {
        return value !== undefined && value < this.shareNominalValue
          ? {
              key: "transactions.issueTransaction.share-price.greater-than-nominal",
              params: { currency: currentCurrency, nominalValue: this.shareNominalValue },
            }
          : undefined;
      },
      required: numberOfShares !== undefined,
      isPositiveOrZero: true,
    });
    this.currency = currentCurrency;
  }

  setShareNominalValue(nominalValue: number) {
    this.shareNominalValue = nominalValue;
  }

  isPriceRequired(isBonusIssue?: boolean) {
    this.price.setRequired(!this.numberOfShares.isEmpty && isBonusIssue !== true);
  }

  get isEmpty(): boolean {
    return this.numberOfShares.isEmpty;
  }

  get isValid(): boolean {
    return this.numberOfShares.isValid && this.price.isValid;
  }

  validate(): boolean {
    this.numberOfShares.validate();
    this.price.validate();
    return this.isValid;
  }
}

export class SubscriberModel {
  id = newUuid();
  shareType: ShareType;
  shareNominalValue: number;
  shareholder: ExtendedShareholderDTO;
  numberOfShares: NumberFieldModel;
  price: NumberFieldModel;
  currency;

  constructor(
    shareType: ShareType,
    shareholder: ExtendedShareholderDTO,
    currentCurrency: string,
    setDirty: () => void,
    shareNominalValue: number,
    numberOfShares?: number,
  ) {
    this.shareType = shareType;
    this.shareNominalValue = shareNominalValue;
    this.shareholder = shareholder;
    this.numberOfShares = new NumberFieldModel({
      onChange: setDirty,
      value: numberOfShares,
      isPositiveOrZero: true,
    });
    this.price = new NumberFieldModel({
      onChange: setDirty,
      isPositiveOrZero: true,
      validator: (value) => {
        return value !== undefined && value < this.shareNominalValue
          ? {
              key: "transactions.issueTransaction.share-price.greater-than-nominal",
              params: { currency: currentCurrency, nominalValue: this.shareNominalValue },
            }
          : undefined;
      },
    });
    this.currency = createCurrencyModel(currentCurrency, setDirty);
    makeAutoObservable(this, {}, { autoBind: true });
  }

  setShareNominalValue(nominalValue: number) {
    this.shareNominalValue = nominalValue;
  }

  get isEmpty(): boolean {
    return this.numberOfShares.isEmpty;
  }

  isValid(isDeviatingPrice?: boolean): boolean {
    return this.numberOfShares.isValid && (isDeviatingPrice ? this.price.isValid : true);
  }

  validate(isDeviatingPrice?: boolean): boolean {
    this.numberOfShares.validate();
    this.price.validate();
    return this.isValid(isDeviatingPrice);
  }

  prepareData(
    isDeviatingPrice: boolean,
    shareIssueModels: ShareTypeIssueModel[],
  ): IssueShareTypeDTO | null {
    const shareIssue = shareIssueModels.find(
      (shareIssueModel) => shareIssueModel.shareType.sharetypeId === this.shareType.sharetypeId,
    );
    const numberOfShares = this.numberOfShares.toNumberOrZero();
    if (!shareIssue || shareIssue.isEmpty || !numberOfShares) {
      return null;
    }

    return {
      sharetypeId: this.shareType.sharetypeId,
      shareholderId: this.shareholder.id,
      numberOfShares: numberOfShares,
      sharePrice: isDeviatingPrice
        ? this.price.toNumberOrZero()
        : shareIssue.price.toNumberOrZero(),
      currency: isDeviatingPrice ? this.currency.value : shareIssue.currency,
    };
  }
}

function initIssuedByRadioModel(
  setDirty: () => void,
  isBonusIssue?: boolean,
): RadioFieldModel<IssuedBy> {
  return new RadioFieldModel<IssuedBy>({
    value: isBonusIssue ? IssuedBy.GENERAL_MEETING : IssuedBy.BOARD_MEETING,
    options: isBonusIssue ? [IssuedBy.GENERAL_MEETING] : Object.values(IssuedBy),
    idFn: identity,
    labelFn: (v) => {
      switch (v) {
        case IssuedBy.BOARD_MEETING:
          return { key: "transactions.issueTransaction.pOa" };
        case IssuedBy.GENERAL_MEETING:
          return { key: "transactions.issueTransaction.generalMeeting" };
      }
    },
    onChange: setDirty,
  });
}

export class IssuedByModel {
  issuedBy: RadioFieldModel<IssuedBy>;
  setDirty: () => void;
  decisionDate: LocalDateFieldModel;

  constructor(setDirty: () => void) {
    makeAutoObservable(this, {}, { autoBind: true });
    this.issuedBy = initIssuedByRadioModel(setDirty);
    this.decisionDate = new LocalDateFieldModel({ onChange: setDirty });
    this.setDirty = setDirty;
  }

  get isValid(): boolean {
    return this.decisionDate.isValid;
  }

  update(isBonusIssue?: boolean) {
    this.issuedBy = initIssuedByRadioModel(this.setDirty, isBonusIssue);
  }

  validate(): boolean {
    this.decisionDate.validate();
    return this.isValid;
  }

  prepareData(): {
    issuedBy?: IssuedBy;
    boardMeetingPoDDate?: string | null;
    generalMeetingDate?: string | null;
  } {
    switch (this.issuedBy.value) {
      case IssuedBy.GENERAL_MEETING:
        return {
          issuedBy: this.issuedBy.value,
          generalMeetingDate: this.decisionDate.validatedValue
            ? combineDateAndTime(this.decisionDate.validatedValue, "12:00")
            : undefined,
        };
      case IssuedBy.BOARD_MEETING:
        return {
          issuedBy: this.issuedBy.value,
          boardMeetingPoDDate: this.decisionDate.validatedValue
            ? combineDateAndTime(this.decisionDate.validatedValue, "12:00")
            : undefined,
        };
      default:
        return {};
    }
  }
}

export type SummaryTableRow = {
  id: string;
  name: TMessage;
  sharesBefore: number;
  sharesAfter: number;
  price: number;
  newShareCapital: number;
  shareCapitalBefore: number;
  noChange: boolean;
};

export function getShareSummaryTableData({
  shareTypeIssues,
  shareNominalValue,
  shareholders,
}: {
  shareTypeIssues: ShareTypeIssueModel[];
  shareNominalValue: number;
  shareholders: ExtendedShareholderDTO[];
}): SummaryTableRow[] {
  const shareSumByType = getShareSumByType(shareholders);
  return shareTypeIssues.map((shareIssue) => {
    const newShares = shareIssue.numberOfShares.toNumberOrZero();
    const sharesBefore = shareSumByType[shareIssue.shareType.sharetypeCode] ?? 0;
    const sharesAfter = sharesBefore + newShares;
    return {
      id: `sharedata-${shareIssue.shareType.sharetypeId}`,
      name: shareTypeLabel(shareIssue.shareType),
      sharesBefore,
      sharesAfter,
      price: shareNominalValue,
      shareCapitalBefore: Big(shareNominalValue).mul(sharesBefore).toNumber(),
      newShareCapital: Big(shareNominalValue).mul(sharesAfter).toNumber(),
      noChange: newShares === 0,
    };
  });
}

type AllocationStatus = { shareType: ShareType; remaining: number; total: number };

export function getAllocationStatus(
  shareTypeIssues: ShareTypeIssueModel[] | ShareTypeBonusIssueModel[],
  subscribers: (SubscriberModel | SubscriberForBonusIssueModel)[],
): AllocationStatus[] {
  return shareTypeIssues
    .filter((shareIssue) => !shareIssue.isEmpty)
    .map((shareIssue) => {
      const total = shareIssue.numberOfShares.toNumberOrZero();
      return {
        shareType: shareIssue.shareType,
        total,
        remaining:
          total -
          subscribers.reduce((sum, subscriber) => {
            if (subscriber.shareType.sharetypeId === shareIssue.shareType.sharetypeId) {
              return sum + subscriber.numberOfShares.toNumberOrZero();
            }
            return sum;
          }, 0),
      };
    });
}

function initSubscribers(
  shareTypes: ShareType[],
  shareholders: ExtendedShareholderDTO[],
  currency: string,
  setDirty: () => void,
  shareNominalValue: number,
  shareholderIdShares?: ShareholderIdShares[],
): SubscriberModel[] {
  return sortBy(shareholders, "name").flatMap((shareholder) => {
    const shareholderData = shareholderIdShares?.find((s) => s.shareholderId === shareholder.id);
    return shareTypes.map((shareType) => {
      const shareData = shareholderData?.shares?.find(
        (s) => s.shareTypeId === shareType.sharetypeId,
      );
      return new SubscriberModel(
        shareType,
        shareholder,
        currency,
        setDirty,
        shareNominalValue,
        shareData?.numberOfShares,
      );
    });
  });
}

function createShareTypeIssueModel(
  shareType: ShareType,
  currency: string,
  setDirty: () => void,
  shareNominalValue: number,
  shareholderIdShares?: ShareholderIdShares[],
) {
  const shares = shareholderIdShares
    ?.flatMap((s) => s.shares ?? [])
    .filter((s) => s.shareTypeId === shareType.sharetypeId);
  const numberOfShares = shares ? sum(shares.map((s) => s.numberOfShares)) : undefined;
  return new ShareTypeIssueModel(shareType, currency, setDirty, shareNominalValue, numberOfShares);
}

export class ShareIssueModel {
  shareNominalValue: number;
  shareTypeIssues: ShareTypeIssueModel[];
  subscribers: SubscriberModel[];
  isDeviatingPrice = new BooleanFieldModel();
  error?: TMessage;
  allocationErrorMessages: TMessage[] = [];

  constructor(
    readonly shareholderData: {
      activeShareTypes: ShareType[];
      shareholders: ExtendedShareholderDTO[];
    },
    readonly companyData: {
      orgNumber: string;
      shareNominalValue: number;
      currency: string;
    },
    private setDirty: () => void,
    private shareholderIdShares?: ShareholderIdShares[],
  ) {
    this.shareNominalValue = companyData.shareNominalValue ?? 0;

    this.subscribers = initSubscribers(
      this.shareTypes,
      this.shareholders,
      this.currency,
      setDirty,
      this.shareNominalValue,
      shareholderIdShares,
    );

    this.shareTypeIssues = this.shareTypes.map((shareType) =>
      createShareTypeIssueModel(
        shareType,
        this.currency,
        setDirty,
        this.shareNominalValue,
        shareholderIdShares,
      ),
    );

    makeAutoObservable(this);
  }

  get shareTypes() {
    return this.shareholderData.activeShareTypes;
  }

  get shareholders() {
    return this.shareholderData.shareholders;
  }

  get currency() {
    return this.companyData.currency;
  }

  onSharetypeAdded(shareType: ShareType) {
    this.shareTypeIssues.push(
      createShareTypeIssueModel(
        shareType,
        this.currency,
        this.setDirty,
        this.shareNominalValue,
        this.shareholderIdShares,
      ),
    );
    this.subscribers = initSubscribers(
      this.shareTypes,
      this.shareholders,
      this.currency,
      this.setDirty,
      this.shareNominalValue,
      this.shareholderIdShares,
    );
  }

  addNewSubscriber(shareholder: ExtendedShareholderDTO) {
    this.subscribers = [
      ...this.subscribers,
      ...this.shareTypes.map(
        (shareType) =>
          new SubscriberModel(
            shareType,
            shareholder,
            this.currency,
            this.setDirty,
            this.shareNominalValue,
          ),
      ),
    ];
  }

  get activeShareTypeIds(): number[] {
    return this.shareTypeIssues
      .filter((s) => !s.numberOfShares.isEmpty)
      .map((s) => s.shareType.sharetypeId);
  }

  get isSubscribers(): boolean {
    return this.subscribers.length > 0;
  }

  get allocatedSubscriberShares(): { [key in ShareTypeCode]?: number } {
    const groupedSubscribers = groupBy(this.subscribers, (e) => e.shareType.sharetypeCode);
    const result: { [key in ShareTypeCode]?: number } = {};
    Object.entries(groupedSubscribers).forEach(([sharetypeCode, subscribers]) => {
      result[sharetypeCode as ShareTypeCode] = subscribers.reduce(
        (p, c) => p + c.numberOfShares.toNumberOrZero(),
        0,
      );
    });
    return result;
  }

  get isValid(): boolean {
    return (
      !this.error &&
      this.isDeviatingPrice.isValid &&
      this.shareTypeIssues.every((s) => s.isValid) &&
      this.subscribers.every((s) => s.isValid()) &&
      getAllocationStatus(this.shareTypeIssues, this.subscribers).every((s) => s.remaining === 0)
    );
  }

  setShareNominalValue(nominalValue: number | undefined) {
    this.shareNominalValue = nominalValue ?? 0;
    this.shareTypeIssues.map((d) => d.setShareNominalValue(this.shareNominalValue));
    this.subscribers.map((d) => d.setShareNominalValue(this.shareNominalValue));
  }

  validate(): boolean {
    const allocatedShares = this.allocatedSubscriberShares;
    this.allocationErrorMessages = this.shareTypeIssues
      .filter((share) => share.numberOfShares.toNumberOrZero() > 0)
      .filter(
        (s) => s.numberOfShares.toNumberOrZero() !== allocatedShares[s.shareType.sharetypeCode],
      )
      .map((value) => ({
        key: "transactions.issueTransaction.number-of-shares.error",
        params: { n: shareTypeLabel(value.shareType.sharetypeCode) },
      }));
    this.isDeviatingPrice.validate();
    this.shareTypeIssues.map((s) => s.validate());
    this.subscribers.map((s) => s.validate(this.isDeviatingPrice.value));

    this.error = this.shareTypeIssues.every((s) => s.isEmpty)
      ? { key: "transactions.issueTransaction.noShareIssueError" }
      : this.subscribers.every((s) => s.isEmpty)
        ? { key: "transactions.issueTransaction.noSubscribersError" }
        : getAllocationStatus(this.shareTypeIssues, this.subscribers).some((s) => s.remaining !== 0)
          ? { key: "transactions.issueTransaction.incompleteSubscribersError" }
          : this.shareNominalValue === 0
            ? { key: "transactions.issueTransaction.nominalValueError" }
            : undefined;
    return this.isValid;
  }

  prepareData(): IssueShareTypeDTO[] {
    logDebug("this.subscribers:", toJS(this.subscribers));
    return compact(
      this.subscribers.map((m) =>
        m.prepareData(this.isDeviatingPrice.value ?? false, this.shareTypeIssues),
      ),
    );
  }

  get excludedShareholders() {
    return this.subscribers.map(
      (subscriber) =>
        (subscriber.shareholder.isCompany
          ? subscriber.shareholder.companyNumber
          : subscriber.shareholder.personInfo?.socialSecurityNumber) ??
        toShareholderReference(subscriber.shareholder.id),
    );
  }

  addNewSubscribers(shareholders: ExtendedShareholderDTO[], onSuccess: () => void) {
    shareholders.map((shareholder) => this.addNewSubscriber(shareholder));
    onSuccess();
  }
}
